import type { SxProps, Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";

import {
  isDarkMode,
  onPrimarySurfaceColor,
  primaryAccentColor,
  unreadCountBadgeColors
} from "@/src/theme/helpers";
import { schemeSx } from "@/src/theme/schemeStyles";
import { lightColors } from "@/src/theme/tokens";
import { getAuctionStatusChipStyles } from "@/src/theme/utils/auctionColors";

import { AuctionState } from "@/src/types/types";

/**
 * Shared resting elevation for the pill buttons in the consumer header.
 *
 * Spelled out rather than read from `muiTheme.shadows[2]` so the Figma spec
 * stays visible here; the two are the same three-layer stack.
 */
export const consumerHeaderButtonShadowSx = {
  boxShadow:
    "0px 1px 5px 0px rgb(0 0 0 / 12%), 0px 2px 2px 0px rgb(0 0 0 / 14%), 0px 3px 1px -2px rgb(0 0 0 / 20%)"
} as const;

/** Shared hover elevation for the pill buttons in the consumer header. */
export const consumerHeaderButtonHoverShadowSx = {
  boxShadow:
    "0px 2px 4px -1px rgb(0 0 0 / 20%), 0px 4px 5px 0px rgb(0 0 0 / 14%), 0px 1px 10px 0px rgb(0 0 0 / 12%)"
} as const;

/** Badge diameter and the width it grows to for three-digit counts. */
const AUCTIONS_COUNT_BADGE_SIZE = "20px";

/**
 * Borderless Primary/Contrast pill shared by the consumer header buttons.
 *
 * Both modes lift the pill off the AppBar with the elevation stack instead of
 * an outline. Light mode carries the brand red label; on the neutral dark
 * AppBar the brand reds only reach ~2.9:1 against the white pill, so dark mode
 * reads the light mode Text/Primary ink.
 *
 * Every state repeats the label color: the dark mode `MuiButton` variants
 * otherwise hand hover and focus a near-white Text/Primary label that vanishes
 * on the white surface.
 */
const consumerHeaderPillSx = schemeSx((muiTheme: Theme) => {
  const darkMode = isDarkMode(muiTheme);
  const labelColor = darkMode
    ? lightColors.text.primary
    : muiTheme.palette.semantic.primary.main;

  return {
    fontWeight: muiTheme.typography.fontWeightBold,
    backgroundColor: lightColors.primary.contrastText,
    color: labelColor,
    borderColor: "transparent",
    borderWidth: 0,
    ...consumerHeaderButtonShadowSx,
    "&:hover": {
      backgroundColor: darkMode
        ? muiTheme.palette.grey[200]
        : lightColors.primary.contrastText,
      color: labelColor,
      borderColor: "transparent",
      borderWidth: 0,
      ...consumerHeaderButtonHoverShadowSx
    },
    "&:focus-visible": {
      backgroundColor: lightColors.primary.contrastText,
      color: labelColor
    }
  } as const;
});

/**
 * "Kilpailutukset" pill in the consumer header.
 *
 * Returns the theme callback itself rather than the `SxProps` union so the
 * dealer header can call it for the dark mode half of its own style.
 */
export const consumerHeaderAuctionsButtonSx = (isLargeScreen: boolean) =>
  schemeSx((muiTheme: Theme): SystemStyleObject<Theme> => {
    return {
      ...consumerHeaderPillSx(muiTheme),
      fontSize: isLargeScreen ? "0.875rem" : "0.75rem",
      p: 1,
      padding: "0px 8px 0px 8px",
      height: isLargeScreen ? "36px" : "32px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      "& .MuiButton-endIcon": {
        marginLeft: 0.5
      }
    };
  });

/** Size shared by the "Kirjaudu" button and the "Kilpailutukset" pill. */
const consumerHeaderLoginButtonSizeSx = {
  fontSize: "0.875rem",
  height: "36px"
} as const;

/** Light mode "Kirjaudu" button: white surface outlined against the red AppBar. */
const consumerHeaderLoginButtonLightSx = schemeSx((muiTheme: Theme) => ({
  fontWeight: muiTheme.typography.fontWeightBold,
  backgroundColor: muiTheme.palette.common.white,
  color: muiTheme.palette.primary.main,
  border: "1px solid",
  borderColor: muiTheme.palette.common.white,
  "&:hover": {
    backgroundColor: muiTheme.palette.grey[200],
    borderColor: muiTheme.palette.grey[200],
    ...consumerHeaderButtonHoverShadowSx
  }
}));

/**
 * "Kirjaudu" button in the consumer header.
 *
 * Dark mode reuses the "Kilpailutukset" pill so both header actions read as
 * one set of white pills on the neutral AppBar.
 */
export const consumerHeaderLoginButtonSx = schemeSx(
  (muiTheme: Theme): SystemStyleObject<Theme> => ({
    ...(isDarkMode(muiTheme)
      ? consumerHeaderPillSx(muiTheme)
      : consumerHeaderLoginButtonLightSx(muiTheme)),
    ...consumerHeaderLoginButtonSizeSx
  })
);

/** Label and count wrapper inside the "Kilpailutukset" pill. */
export const consumerHeaderAuctionsLabelSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 0.75,
  flex: 1
};

/**
 * Active auction count bubble inside the "Kilpailutukset" pill.
 *
 * The pill is white in both modes, so the bubble inverts it with the light
 * mode Text/Primary fill and a white label regardless of the active mode.
 */
export const consumerHeaderAuctionsCountSx = (count: number): SxProps<Theme> =>
  schemeSx((muiTheme) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: AUCTIONS_COUNT_BADGE_SIZE,
    width: count > 9 ? "auto" : AUCTIONS_COUNT_BADGE_SIZE,
    height: AUCTIONS_COUNT_BADGE_SIZE,
    borderRadius: "50%",
    backgroundColor: lightColors.text.primary,
    color: muiTheme.palette.common.white,
    fontSize: muiTheme.typography.caption.fontSize,
    fontWeight: muiTheme.typography.fontWeightBold,
    px: count > 9 ? 0.75 : 0,
    lineHeight: 1
  }));

/**
 * Unread count below the desktop breakpoint, where the notification bell
 * collapses into the hamburger and the mobile menu's notification row.
 */
export const consumerHeaderUnreadBadgeSx: SxProps<Theme> = schemeSx(
  (muiTheme) => ({
    "& .MuiBadge-badge": unreadCountBadgeColors(muiTheme)
  })
);

/** Viewport width from which the consumer header shows the desktop row. */
export const CONSUMER_HEADER_DESKTOP_QUERY = "(min-width:910px)";

/**
 * Hides the hamburger on desktop widths through CSS. Used while auth resolves:
 * that state is what gets prerendered and hydrated, before `useMediaQuery`
 * knows the viewport, so it must look right at any width on its own.
 */
export const consumerHeaderMobileOnlySx: SxProps<Theme> = {
  [`@media ${CONSUMER_HEADER_DESKTOP_QUERY}`]: { display: "none" }
};

/** Counterpart of `consumerHeaderMobileOnlySx`, for the desktop-only controls. */
export const consumerHeaderDesktopOnlySx: SxProps<Theme> = {
  display: "none",
  alignSelf: "stretch",
  alignItems: "center",
  // Same gap as the actions row, which separates the divider and the toggle.
  gap: 1,
  [`@media ${CONSUMER_HEADER_DESKTOP_QUERY}`]: { display: "flex" }
};

/**
 * Row holding the desktop header actions: the nav items, the pill buttons, the
 * divider and the theme toggle. One gap here keeps the whole row on the 8px
 * rhythm instead of each action carrying its own right margin.
 */
export const consumerHeaderActionsSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1
};

/**
 * The icon buttons inside the actions row.
 *
 * They already pad their 24px glyph out to a 40px target, so they sit flush
 * against each other: the padding alone leaves the glyphs 16px apart, and the
 * row's own gap on top of that would read as a break between them.
 *
 * The negative right margin takes back the 8px gap that the trailing button's
 * padding already provides, so the last glyph sits as far from the divider as
 * the toggle does on the other side instead of pushing the divider outward.
 */
export const consumerHeaderIconGroupSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  mr: -1
};

/**
 * Vertical rule between the desktop header items and the theme toggle.
 *
 * `divider` is tuned for content surfaces and disappears against the AppBar,
 * so the rule is drawn from the on-AppBar ink instead. The margin matches the
 * actions row gap so the rule sits centered between the two groups.
 */
const headerThemeToggleDividerStyles = schemeSx((muiTheme: Theme) => ({
  mx: 1,
  my: 1,
  borderColor: alpha(onPrimarySurfaceColor(muiTheme), 0.3)
}));

export const headerThemeToggleDividerSx: SxProps<Theme> =
  headerThemeToggleDividerStyles;

/**
 * The consumer header's version of the rule, at a fixed 24px instead of
 * stretching with the row.
 *
 * The signed-in row holds the bell and account buttons, whose glyphs scale
 * with the theme's rem base to a 43px target. A stretched rule would grow from
 * the 24px it has in the 40px auth-pending and signed-out rows the moment the
 * header finishes loading.
 */
export const consumerHeaderDividerSx: SxProps<Theme> = schemeSx(
  (muiTheme: Theme) => ({
    ...headerThemeToggleDividerStyles(muiTheme),
    my: 0,
    alignSelf: "center",
    height: 24
  })
);

/** Dropdown surface listing the consumer's own auctions. */
export const consumerHeaderAuctionsMenuPaperSx: SxProps<Theme> = {
  maxHeight: 400,
  width: 320,
  mt: 1
};

/** "View all" and "Add new auction" actions above the auction list. */
export const consumerHeaderAuctionsMenuActionsSx: SxProps<Theme> = {
  p: 1.5,
  display: "flex",
  flexDirection: "column",
  gap: 1
};

/**
 * "View all auctions" pill at the top of the auctions dropdown.
 *
 * Dark mode Misc/Timeline Background falls back to Background/Paper, which is
 * the dropdown surface itself, so the pill reads an elevation surface instead
 * to stay visible against the menu.
 */
export const consumerHeaderViewAllAuctionsSx: SxProps<Theme> = schemeSx(
  (muiTheme) => {
    const darkMode = isDarkMode(muiTheme);

    return {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      py: 0.875,
      px: 2,
      borderRadius: "2rem",
      backgroundColor: darkMode
        ? muiTheme.palette.misc.elevation2
        : muiTheme.palette.misc.timelineBackground,
      border: "1px solid",
      borderColor: darkMode
        ? muiTheme.palette.misc.outlineBorder
        : muiTheme.palette.misc.coldBackground,
      transition: "all 0.2s ease",
      cursor: "pointer",
      "&:hover": {
        backgroundColor: darkMode
          ? muiTheme.palette.misc.elevation4
          : muiTheme.palette.misc.coldBackground,
        borderColor: primaryAccentColor(muiTheme),
        cursor: "pointer"
      }
    };
  }
);

/** Label inside the "View all auctions" pill. */
export const consumerHeaderViewAllAuctionsLabelSx: SxProps<Theme> = schemeSx(
  (muiTheme) => ({
    fontWeight: muiTheme.typography.fontWeightSemibold,
    color: muiTheme.palette.text.primary,
    textAlign: "center",
    pointerEvents: "none"
  })
);

/** "Add new auction" call to action, disabled until the email is verified. */
export const consumerHeaderNewAuctionButtonSx = (
  isEmailVerified: boolean
): SxProps<Theme> =>
  schemeSx((muiTheme) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.75,
    py: 0.875,
    px: 2,
    borderRadius: "2rem",
    backgroundColor: isEmailVerified
      ? muiTheme.palette.semantic.primary.main
      : muiTheme.palette.action.disabledBackground,
    transition: "all 0.2s ease",
    opacity: isEmailVerified ? 1 : 0.6,
    cursor: isEmailVerified ? "pointer" : "not-allowed",
    "&:hover": {
      backgroundColor: isEmailVerified
        ? muiTheme.palette.semantic.primary.dark
        : muiTheme.palette.action.disabledBackground,
      transform: isEmailVerified ? "translateY(-1px)" : "none",
      boxShadow: isEmailVerified
        ? `0 2px 10px ${muiTheme.palette.divider}`
        : "none",
      cursor: isEmailVerified ? "pointer" : "not-allowed"
    }
  }));

/** Icon inside the "Add new auction" call to action. */
export const consumerHeaderNewAuctionIconSx: SxProps<Theme> = schemeSx(
  (muiTheme) => ({
    fontSize: 16,
    color: onPrimarySurfaceColor(muiTheme),
    pointerEvents: "none"
  })
);

/** Label inside the "Add new auction" call to action. */
export const consumerHeaderNewAuctionLabelSx: SxProps<Theme> = schemeSx(
  (muiTheme) => ({
    fontWeight: muiTheme.typography.fontWeightBold,
    color: onPrimarySurfaceColor(muiTheme),
    textAlign: "center",
    pointerEvents: "none"
  })
);

/** "Active", "Accepted" and "Drafts" group headers in the auctions dropdown. */
export const consumerHeaderAuctionGroupLabelSx: SxProps<Theme> = schemeSx(
  (muiTheme) => ({
    fontWeight: muiTheme.typography.fontWeightBold,
    color: muiTheme.palette.text.secondary
  })
);

/** Registration number and status chip stack inside one auction menu item. */
export const consumerHeaderAuctionItemSx = (
  hasDeleteAction: boolean
): SxProps<Theme> => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  py: 0.5,
  ...(hasDeleteAction ? { pr: 4 } : {})
});

/** Vehicle label of a single auction menu item. */
export const consumerHeaderAuctionItemLabelSx: SxProps<Theme> = schemeSx(
  (muiTheme) => ({
    fontWeight: muiTheme.typography.fontWeightMedium
  })
);

/** Status chip of a single auction menu item. */
export const consumerHeaderAuctionChipSx = (
  state: AuctionState,
  hasPendingOffer: boolean
): SxProps<Theme> =>
  schemeSx((muiTheme) => ({
    height: 20,
    fontSize: muiTheme.typography.caption.fontSize,
    fontWeight: muiTheme.typography.fontWeightSemibold,
    ...getAuctionStatusChipStyles(muiTheme, state, hasPendingOffer)
  }));

/** Delete button on a draft auction menu item. */
export const consumerHeaderDeleteDraftSx: SxProps<Theme> = schemeSx(
  (muiTheme) => ({
    position: "absolute",
    right: 8,
    top: "50%",
    transform: "translateY(-50%)",
    color: muiTheme.palette.semantic.error.main,
    "&:hover": {
      bgcolor: muiTheme.palette.semantic.error.main,
      color: muiTheme.palette.semantic.error.contrastText
    }
  })
);
